import { alpha, styled } from '@mui/material';
import React from 'react';
import { VariantComponentProps } from './variant';
import Adornment from './Adornment';
import { Input as BaseInput } from './Input';

const Input = styled(BaseInput)(({ theme }) => ({
    'fontSize': theme.typography.body2.fontSize,

    '&:not(:disabled):read-only': {
        userSelect: 'none', // Prevents selection
        pointerEvents: 'unset', // Disables mouse interaction
    },
}));

const Base = styled('div', {
    shouldForwardProp: prop =>
        prop !== 'disabled' &&
        prop !== 'error' &&
        prop !== 'focused' &&
        prop !== 'hovered' &&
        prop !== 'height' &&
        prop !== 'backgroundColor' &&
        prop !== 'sx',
})<{
    disabled?: boolean;
    error?: boolean;
    focused: boolean;
    hovered: boolean;
    height: number;
    backgroundColor?: string;
}>(({ theme, disabled, error, focused, hovered, height, backgroundColor }) => ({
    'position': 'relative',
    'width': '100%',
    'display': 'flex',
    'justifyContent': 'flex-start',
    'alignItems': 'center',
    'borderRadius': '0px',
    'borderWidth': '0px',
    'borderColor': `rgba(0, 0, 0, 0.23)`,
    'backgroundColor': backgroundColor || theme.palette.background.paper,
    'overflow': 'hidden',
    'height': height,
    'cursor': 'text',

    ...(focused && {
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary[50], 0.4),
    }),

    ...(hovered && {
        backgroundColor: alpha(theme.palette.primary[50], 0.6),
    }),

    ...(disabled && {
        backgroundColor: 'transparent',
        cursor: 'auto !important',
    }),

    ...(error && {
        borderColor: theme.palette.error.main,
    }),

    '& > input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.paper} inset`,
    },
    '& > input:-webkit-autofill:hover': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.paper} inset`,
    },
    '& > input:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.paper} inset`,
    },
    '& > input:-webkit-autofill:active': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.paper} inset`,
    },
}));

// Provides the border. Having the border separate from the base makes it easier to handle the border width change on focus.
// It inherits the styling of the border from the base so it can be easily customized.
const Border = styled('div', {
    shouldForwardProp: prop =>
        prop !== 'focused' && prop !== 'error' && prop !== 'sx',
})<{
    focused: boolean;
    readOnly?: boolean;
    error?: boolean;
}>(({ focused, readOnly, error }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 'inherit',
    borderColor: 'inherit',
    borderWidth: '0px',
    borderStyle: 'dashed',
    pointerEvents: 'none',
    ...((focused || error) && {
        borderWidth: '2px',
        borderStyle: !readOnly ? 'solid' : 'dashed',
    }),
}));

const SheetInput = React.forwardRef<HTMLInputElement, VariantComponentProps>(
    (
        {
            // Core behavior props
            height = 35,
            error,
            disabled,

            // Styling props
            backgroundColor,
            sx,
            inputSx,

            // UI elements inside the input
            startAdornment,
            startAdornmentRef,
            startAdornmentWidth,
            startAdornmentSx,
            endAdornment,
            endAdornmentRef,
            endAdornmentWidth,
            endAdornmentSx,

            // Event handlers for <Base>
            onBaseClick,
            onBaseMouseEnter,
            onBaseMouseLeave,

            // Event handlers
            onChange,
            onFocus,
            onBlur,
            onMouseEnter,
            onMouseLeave,
            onKeyDown,

            // Other input-related props
            readOnly: readOnlyProp,
            type,
            ...props
        },
        externalRef
    ) => {
        const [focused, setFocused] = React.useState(false);
        const [hovered, setHovered] = React.useState(false);
        const [readOnly, setReadOnly] = React.useState(
            type === 'time' ? false : true
        );

        const inputRef = React.useRef<HTMLInputElement | null>(null);

        // If externalRef is provided, pass it down to allow forwarding
        React.useImperativeHandle(externalRef, () => inputRef.current!);

        const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            if (onFocus) {
                onFocus(e);
            }
        };

        const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            inputRef.current?.setSelectionRange(
                inputRef.current.value.length,
                inputRef.current.value.length
            ); // Resets the cursor position to the end of the input

            if (type === 'time') {
                setReadOnly(true);
            }
            if (onBlur) {
                onBlur(e);
            }
        };

        // Handle clicking on <Base> to focus the input
        const handleBaseClick = (e: React.MouseEvent<HTMLDivElement>) => {
            setReadOnly(false); // Allow typing
            inputRef.current?.focus(); // Manually focus input
            onBaseClick?.(e); // Call user-defined handler if provided
        };

        const handleMouseEnter = (e: React.MouseEvent<HTMLInputElement>) => {
            setHovered(true);
            onMouseEnter?.(e);
        };
        const handleMouseLeave = (e: React.MouseEvent<HTMLInputElement>) => {
            setHovered(false);
            onMouseLeave?.(e);
        };

        const handleOnKeyDown = (
            event: React.KeyboardEvent<HTMLInputElement>
        ) => {
            onKeyDown?.(event); // Call user-defined handler if provided
            if (event.nativeEvent.defaultPrevented) return; // Skip if stopped

            const { key, currentTarget } = event;

            if (key.startsWith('Arrow')) {
                // 🔥 Get current row and column index
                const currentCell = currentTarget.closest('td'); // Find parent <td>
                const currentRow = currentTarget.closest('tr'); // Find parent <tr>
                if (!currentCell || !currentRow) return;

                const columnIndex = Array.from(currentRow.children).indexOf(
                    currentCell
                );
                const allRows = Array.from(
                    currentRow.parentElement?.children || []
                );

                const currentRowIndex = allRows.indexOf(currentRow);

                // Get current input index within the <td>
                const inputsInCell = Array.from(
                    currentCell.querySelectorAll('input:not(:disabled)')
                );
                const currentInputIndex = inputsInCell.indexOf(currentTarget);

                const cursorPos = currentTarget.selectionStart;
                const valueLength = currentTarget.value.length;

                // 🔥 Function to find the next/previous enabled input **inside the same <td>**
                const findNextInputInSameTd = () => {
                    const input = inputsInCell[currentInputIndex + 1] || null;
                    return input as HTMLInputElement | null;
                };

                const findPrevInputInSameTd = () => {
                    const input = inputsInCell[currentInputIndex - 1] || null;
                    return input as HTMLInputElement | null;
                };

                // 🔥 Function to find the next enabled input in another `<td>`
                // Used for moving up/down and right.
                const findNextEnabledInput = (
                    element: HTMLElement | null,
                    preferredIndex = 0 // If there are multiple inputs in the same `<td>`, we can specify which one to use
                ) => {
                    while (element) {
                        const inputs = Array.from(
                            element.querySelectorAll('input:not(:disabled)')
                        );

                        // if length is at least preferredIndex we use that index
                        if (inputs.length > preferredIndex) {
                            return inputs[preferredIndex] as HTMLInputElement;
                        } else if (inputs.length > 0) {
                            return inputs[0] as HTMLInputElement;
                        }

                        element =
                            element.nextElementSibling as HTMLElement | null;
                    }
                    return null;
                };

                // 🔥 Function to find the previous enabled input in another `<td>`
                // Only used when moving to the left
                const findPrevEnabledInput = (element: HTMLElement | null) => {
                    while (element) {
                        const inputs = Array.from(
                            element.querySelectorAll('input:not(:disabled)')
                        );
                        if (inputs.length > 0) {
                            return inputs[
                                inputs.length - 1
                            ] as HTMLInputElement;
                        }
                        element =
                            element.previousElementSibling as HTMLElement | null;
                    }
                    return null;
                };

                // 🔥 Move Down (Same Column, Next Row)
                if (key === 'ArrowDown') {
                    event.preventDefault();
                    let nextRowIndex = currentRowIndex + 1;
                    let nextInput: HTMLInputElement | null = null;

                    while (nextRowIndex < allRows.length && !nextInput) {
                        const nextRow = allRows[nextRowIndex];
                        nextInput = nextRow
                            ? findNextEnabledInput(
                                  nextRow.children[columnIndex] as HTMLElement,
                                  currentInputIndex
                              )
                            : null;
                        nextRowIndex++;
                    }

                    nextInput?.focus();
                    return;
                }

                // 🔥 Move Up (Same Column, Previous Row)
                if (key === 'ArrowUp') {
                    event.preventDefault();
                    let prevRowIndex = currentRowIndex - 1;
                    let prevInput: HTMLInputElement | null = null;

                    while (prevRowIndex >= 0 && !prevInput) {
                        const prevRow = allRows[prevRowIndex];
                        prevInput = prevRow
                            ? findNextEnabledInput(
                                  prevRow.children[columnIndex] as HTMLElement,
                                  currentInputIndex
                              )
                            : null;
                        prevRowIndex--;
                    }

                    prevInput?.focus();
                    return;
                }

                // 🔥 Move Left (Previous Input in Same `<td>` or Move to Previous `<td>`)
                if (key === 'ArrowLeft' && (cursorPos === 0 || readOnly)) {
                    event.preventDefault();

                    // Try to move to the previous input inside the same `<td>`
                    const prevInput = findPrevInputInSameTd();
                    if (prevInput) {
                        prevInput.focus();
                        return;
                    }

                    // Otherwise, move to the previous `<td>`
                    const leftInput = findPrevEnabledInput(
                        currentRow.children[columnIndex - 1] as HTMLElement
                    );
                    leftInput?.focus();
                    return;
                }

                // 🔥 Move Right (Next Input in Same `<td>` or Move to Next `<td>`)
                if (
                    key === 'ArrowRight' &&
                    (cursorPos === valueLength || readOnly)
                ) {
                    event.preventDefault();

                    // Try to move to the next input inside the same `<td>`
                    const nextInput = findNextInputInSameTd();
                    if (nextInput) {
                        nextInput.focus();
                        return;
                    }

                    // Otherwise, move to the next `<td>`
                    const rightInput = findNextEnabledInput(
                        currentRow.children[columnIndex + 1] as HTMLElement,
                        0
                    );
                    rightInput?.focus();
                    return;
                }
            }

            // 🚀 Handle Enter: Toggle Read-Only Mode
            if (key === 'Enter') {
                event.preventDefault();
                if (readOnly) {
                    // Resets the cursor position to the end of the input
                    currentTarget.setSelectionRange(
                        currentTarget.value.length,
                        currentTarget.value.length
                    );
                }
                handleToggleReadOnly();
                return;
            }

            // 🚀 Handle Delete: Clear input
            if (key === 'Delete' && readOnly) {
                event.preventDefault();
                currentTarget.value = '';
                onChange?.(
                    event as unknown as React.ChangeEvent<HTMLInputElement>
                );
                handleToggleReadOnly();
                return;
            }

            // 🚀 If user types anything while in read-only mode, it will leave it.
            if (readOnly) {
                // Resets the cursor position to the end of the input
                currentTarget.setSelectionRange(
                    currentTarget.value.length,
                    currentTarget.value.length
                );
                setReadOnly(false); // Enable editing
                return;
            }
        };

        const handleToggleReadOnly = () => {
            setReadOnly(prev => (type === 'time' ? false : !prev));
        };

        return (
            <Base
                disabled={disabled}
                error={error}
                focused={focused}
                hovered={hovered}
                height={height}
                backgroundColor={backgroundColor}
                sx={sx}
                onClick={handleBaseClick}
                onMouseEnter={onBaseMouseEnter}
                onMouseLeave={onBaseMouseLeave}>
                <Input
                    {...props}
                    ref={inputRef}
                    type={type}
                    disabled={disabled}
                    onChange={onChange}
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onKeyDown={handleOnKeyDown}
                    startAdornmentWidth={startAdornmentWidth}
                    endAdornmentWidth={endAdornmentWidth}
                    basePadding={8}
                    sx={inputSx}
                    readOnly={readOnly || readOnlyProp}
                />
                <Border
                    focused={focused}
                    readOnly={readOnly || readOnlyProp}
                    error={error}
                />
                {startAdornment && (
                    <Adornment
                        ref={startAdornmentRef}
                        position='start'
                        sx={startAdornmentSx}>
                        {startAdornment}
                    </Adornment>
                )}
                {endAdornment && (
                    <Adornment
                        ref={endAdornmentRef}
                        position='end'
                        sx={endAdornmentSx}>
                        {endAdornment}
                    </Adornment>
                )}
            </Base>
        );
    }
);
SheetInput.displayName = 'SheetInput';

export default SheetInput;
